import React, { useRef, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Container, Sprite } from "@pixi/react";
import { useSelector } from "react-redux";
import ReelFrontController, {
  STATIC_CONFIG,
  // STATIC_CONFIG_FEATURE,n
  SPINE_CONFIG,
} from "./ReelFrontController";
import { Settings } from "../../../settings";
import { selectHoldAndWinActive } from "../../../State/selectors";
import * as PIXI from "pixi.js";
import { ASSET_NAME } from "../../../assets";
import LogoLandscape from "../../../Components/UserInterface/shared/Logos/LogoLandscape";
import LogoPortrait from "../../../Components/UserInterface/shared/Logos/LogoPortrait";

const ReelFront = ({
  symbolWidth,
  symbolHeight,
  dimensions,
  dividerDimensions,
  renderMode = "spine", // Changed default from "static" to "spine"
}) => {
  const { t } = useTranslation();
  const reelFrontControllerRef = useRef(null);
  const spriteRef = useRef(null);
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  // Initialize ReelFrontController when assets are ready (spine mode only)
  useEffect(() => {
    if (
      renderMode === "spine" &&
      spriteRef.current &&
      !reelFrontControllerRef.current
    ) {
      reelFrontControllerRef.current = new ReelFrontController(
        spriteRef.current,
        symbolWidth,
        symbolHeight,
        dimensions,
        dividerDimensions,
        t,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [renderMode]);

  // Handle dimension changes for responsive design (spine mode only)
  useEffect(() => {
    if (renderMode === "spine" && reelFrontControllerRef.current) {
      reelFrontControllerRef.current.resizeReelFront(
        symbolWidth,
        symbolHeight,
        dimensions,
        dividerDimensions,
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbolHeight, renderMode]);

  // Memoize divider count - constant value
  const dividerCount = useMemo(() => Settings.REEL_COUNT - 1, []);

  // Memoize divider width
  const dividerWidth = useMemo(
    () => dividerDimensions?.width ?? 0,
    [dividerDimensions?.width],
  );

  // Always use base config (decorations are now separate component)
  // Config is constant, no need to memoize
  const config = STATIC_CONFIG;

  // Memoize static dimensions calculations
  const staticWidth = useMemo(
    () =>
      (symbolWidth * Settings.REEL_COUNT + dividerWidth * dividerCount) *
      config.widthMultiplier,
    [symbolWidth, dividerWidth, dividerCount, config.widthMultiplier],
  );

  const staticHeight = useMemo(
    () => symbolHeight * Settings.SYMBOLS_VISIBLE * config.heightMultiplier,
    [symbolHeight, config.heightMultiplier],
  );

  // Memoize texture lookup for static mode
  const staticTexture = useMemo(() => {
    if (renderMode !== "static") return null;
    return PIXI.Assets.get(ASSET_NAME.reel_frame_front_static);
  }, [renderMode]);

  // Memoize container position calculations
  const containerPosition = useMemo(() => {
    if (renderMode === "spine") {
      return {
        x: symbolWidth * Settings.REEL_COUNT * SPINE_CONFIG.horizontalOffset,
        y:
          -symbolHeight *
          Settings.SYMBOLS_VISIBLE *
          SPINE_CONFIG.verticalOffset,
      };
    }
    return {
      x: config.horizontalOffset,
      y: config.verticalOffset,
    };
  }, [
    renderMode,
    symbolWidth,
    symbolHeight,
    config.horizontalOffset,
    config.verticalOffset,
  ]);

  // Memoize zIndex calculation
  const containerZIndex = useMemo(
    () => (holdAndWinActive ? 6 : 4),
    [holdAndWinActive],
  );

  return (
    <Container
      y={containerPosition.y}
      x={containerPosition.x}
      ref={spriteRef}
      name="front_texture"
      zIndex={containerZIndex}
      sortableChildren={true}
    >
      <LogoPortrait renderMode="static" />

      <LogoLandscape renderMode="static" />

      {renderMode === "static" && staticTexture && (
        <Sprite
          texture={staticTexture}
          width={staticWidth}
          height={staticHeight}
          zIndex={4}
          anchor={0.5}
        />
      )}
    </Container>
  );
};

export default ReelFront;
